import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getDashboardSummary } from "../api";
import "../assets/css/windows.css";
import "../assets/css/dashboard.css";

const emptyDashboard = {
  context: { date: "", selectedSite: "", sites: [] },
  highlights: [],
  quickFacts: [],
  alerts: [],
  occupancyTrend: [],
  revenueTrend: [],
  statusDistribution: [],
  financial: {},
  fiscalMix: [],
  vatBreakdown: [],
  topServices: [],
  quotes: {},
  customers: {},
  rooms: { housekeeping: {} },
  upcomingEvents: [],
  activities: [],
  panels: [],
};


const STATUS_LABELS = {
  PENDING: "In Attesa",
  CONFIRMED: "Confermata",
  CHECKED_IN: "In House",
  CHECKED_OUT: "Conclusa",
  CANCELLED: "Annullata",
};

function localDateTimeParam() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hour = String(now.getHours()).padStart(2, "0");
  const minute = String(now.getMinutes()).padStart(2, "0");
  const second = String(now.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
}

function formatMoney(value) {
  return `${Number(value || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

function formatNumber(value, decimals = 0) {
  return Number(value || 0).toLocaleString("it-IT", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function toneClass(value) {
  const tone = String(value || "").trim().toLowerCase();
  if (["high", "alta"].includes(tone)) return "is_high";
  if (["medium", "media"].includes(tone)) return "is_medium";
  if (["ok", "regolare", "bassa"].includes(tone)) return "is_ok";
  return "is_low";
}

function MetricRows({ rows = [] }) {
  if (!rows.length) return <div className="dashboard_empty_state">Nessun dato disponibile</div>;
  return (
    <div className="dashboard_metric_rows">
      {rows.map((row) => (
        <div className="dashboard_metric_row" key={row.label}>
          <span>{row.label}</span>
          <strong>{row.value}</strong>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const [dashboardData, setDashboardData] = useState(emptyDashboard);
  const [selectedSite, setSelectedSite] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const loadDashboard = useCallback(async ({ foreground = false } = {}) => {
    if (foreground) setLoading(true);
    else setRefreshing(true);
    setError("");
    try {
      const data = await getDashboardSummary({
        site: selectedSite || undefined,
        date: localDateTimeParam(),
        now: localDateTimeParam(),
      });
      setDashboardData({ ...emptyDashboard, ...(data || {}) });
    } catch {
      setError("Impossibile caricare i dati aggiornati della Dashboard");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedSite]);

  useEffect(() => {
    loadDashboard({ foreground: true });
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") loadDashboard();
    }, 60000);
    return () => window.clearInterval(timer);
  }, [loadDashboard]);

  const sites = Array.isArray(dashboardData.context?.sites) ? dashboardData.context.sites : [];
  const financial = dashboardData.financial || {};
  const customers = dashboardData.customers || {};
  const rooms = dashboardData.rooms || {};
  const housekeeping = rooms.housekeeping || {};
  const quotes = dashboardData.quotes || {};

  const customerRows = [
    { label: "Anagrafiche", value: formatNumber(customers.total) },
    { label: "Attive", value: formatNumber(customers.active) },
    { label: "Aziende / DI / PA", value: formatNumber(customers.companies) },
    { label: "VIP", value: formatNumber(customers.vip) },
    { label: "Bloccate", value: formatNumber(customers.blocked) },
    { label: "Senza email", value: formatNumber(customers.missingEmail) },
    { label: "Senza telefono", value: formatNumber(customers.missingPhone) },
  ];

  const quoteRows = [
    { label: "Aperti", value: formatNumber(quotes.draft) },
    { label: "Valore aperto", value: formatMoney(quotes.openValue) },
    { label: "In scadenza 7 gg", value: formatNumber(quotes.expiring) },
    { label: "Scaduti", value: formatNumber(quotes.expired) },
  ];

  const roomRows = [
    { label: "Camere totali", value: formatNumber(rooms.total) },
    { label: "Disponibili ora", value: formatNumber(rooms.availableNow) },
    { label: "Occupate ora", value: formatNumber(rooms.occupiedNow) },
    { label: "Pulite", value: formatNumber(housekeeping.clean) },
    { label: "Da pulire", value: formatNumber(housekeeping.dirty) },
    { label: "Da controllare", value: formatNumber(housekeeping.toCheck) },
    { label: "Indisponibili", value: formatNumber(housekeeping.unavailable) },
  ];

  const financeRows = [
    { label: "Imponibile mese", value: formatMoney(financial.taxable) },
    { label: "IVA mese", value: formatMoney(financial.tax) },
    { label: "Lordo mese", value: formatMoney(financial.gross) },
    { label: "Incassato mese", value: formatMoney(financial.paid) },
    { label: "Da incassare", value: formatMoney(financial.outstanding) },
    { label: "Ticket medio", value: formatMoney(financial.averageTicket) },
    { label: "Documenti attivi", value: formatNumber(financial.activeDocuments) },
    { label: "Documenti annullati", value: formatNumber(financial.cancelledDocuments) },
  ];

  return (
    <section className={`window_page dashboard_page${loading ? " is_loading" : ""}`}>
      <div className="window_banner">
        <div className="window_banner_copy">
          <h1>DASHBOARD</h1>
          <p>Controllo operativo, commerciale e amministrativo della struttura con dati reali aggiornati dal gestionale.</p>
        </div>
      </div>

      <div className="dashboard_control_bar">
        <label className="dashboard_control_field">
          <span>Sede</span>
          <select value={selectedSite} onChange={(event) => setSelectedSite(event.target.value)}>
            <option value="">Tutte le sedi</option>
            {sites.map((site) => <option key={site.id || site.description} value={site.description}>{site.description}</option>)}
          </select>
        </label>
        <div className="dashboard_control_meta">
          <span>Aggiornamento automatico ogni 60 secondi</span>
          <strong>{dashboardData.generatedAt ? `Ultimo aggiornamento ${formatDateTime(dashboardData.generatedAt)}` : "Dati in caricamento"}</strong>
        </div>
        <button type="button" className="dashboard_refresh_button" onClick={() => loadDashboard()} disabled={loading || refreshing}>
          {loading || refreshing ? "Aggiornamento..." : "Aggiorna"}
        </button>
      </div>

      {error && <div className="dashboard_status_message">{error}</div>}

      <div className="dashboard_highlights_grid">
        {(loading ? Array.from({ length: 8 }, (_, index) => ({ loading: true, title: `loading-${index}` })) : (dashboardData.highlights || [])).map((card, index) => (
          <article key={card.loading ? `dashboard-loading-${index}` : card.title} className="dashboard_highlight_card">
            {!card.loading && <>
              <span className="dashboard_highlight_title">{card.title}</span>
              <strong>{card.value}</strong>
              <p>{card.detail}</p>
              <small>{card.trend}</small>
            </>}
          </article>
        ))}
      </div>

      <section className="dashboard_section_card dashboard_alert_section">
        <div className="dashboard_section_header">
          <div>
            <span className="dashboard_section_kicker">CONTROLLO</span>
            <h2>Da controllare</h2>
          </div>
          <span className="dashboard_section_period">Adesso</span>
        </div>
        <div className="dashboard_alert_grid">
          {(dashboardData.alerts || []).map((alert, index) => (
            <Link to={alert.page || "/app/dashboard"} className={`dashboard_alert_card ${toneClass(alert.tone)}`} key={`${alert.title}-${index}`}>
              <strong>{alert.title}</strong>
              <span>{alert.detail}</span>
            </Link>
          ))}
        </div>
      </section>

      <div className="dashboard_two_column_layout">
        <section className="dashboard_section_card">
          <div className="dashboard_section_header">
            <div>
              <span className="dashboard_section_kicker">OPERATIVITÀ</span>
              <h2>Occupazione prossimi 7 giorni</h2>
            </div>
            <span className="dashboard_section_period">Forecast</span>
          </div>
          <div className="dashboard_progress_list">
            {(dashboardData.occupancyTrend || []).map((day) => (
              <div className="dashboard_progress_row" key={day.date}>
                <div className="dashboard_progress_copy">
                  <strong>{day.label}</strong>
                  <span>{day.occupied}/{day.available} camere · {day.arrivals} arrivi · {day.departures} partenze</span>
                </div>
                <progress value={Number(day.rate || 0)} max="100" aria-label={`Occupazione ${day.label}`} />
                <b>{formatNumber(day.rate, 1)}%</b>
              </div>
            ))}
          </div>
        </section>

        <section className="dashboard_section_card">
          <div className="dashboard_section_header">
            <div>
              <span className="dashboard_section_kicker">FISCALE</span>
              <h2>Ultimi 7 giorni</h2>
            </div>
            <span className="dashboard_section_period">Documenti emessi</span>
          </div>
          <div className="dashboard_compact_table">
            <div className="dashboard_compact_table_head"><span>Giorno</span><span>Doc.</span><span>Lordo</span><span>Incassato</span></div>
            {(dashboardData.revenueTrend || []).map((day) => (
              <div className="dashboard_compact_table_row" key={day.date}>
                <span>{day.label}</span><span>{day.documents}</span><strong>{formatMoney(day.gross)}</strong><strong>{formatMoney(day.paid)}</strong>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="dashboard_three_column_layout">
        <section className="dashboard_section_card">
          <div className="dashboard_section_header">
            <div><span className="dashboard_section_kicker">PRENOTAZIONI</span><h2>Stati</h2></div>
            <Link to="/app/bookings" className="dashboard_section_link">Apri Planning</Link>
          </div>
          <div className="dashboard_status_list">
            {(dashboardData.statusDistribution || []).map((item) => (
              <div className="dashboard_status_line" key={item.key}>
                <span className={`dashboard_status_dot is_${String(item.key || "").toLowerCase()}`}></span>
                <span>{item.label}</span>
                <strong>{item.count}</strong>
              </div>
            ))}
          </div>
        </section>

        <section className="dashboard_section_card">
          <div className="dashboard_section_header">
            <div><span className="dashboard_section_kicker">CAMERE</span><h2>Housekeeping</h2></div>
            <Link to="/app/rooms" className="dashboard_section_link">Apri Camere</Link>
          </div>
          <MetricRows rows={roomRows} />
        </section>

        <section className="dashboard_section_card">
          <div className="dashboard_section_header">
            <div><span className="dashboard_section_kicker">COMMERCIALE</span><h2>Preventivi</h2></div>
            <Link to="/app/quotes" className="dashboard_section_link">Apri Preventivi</Link>
          </div>
          <MetricRows rows={quoteRows} />
        </section>
      </div>

      <div className="dashboard_two_column_layout dashboard_finance_layout">
        <section className="dashboard_section_card">
          <div className="dashboard_section_header">
            <div><span className="dashboard_section_kicker">AMMINISTRAZIONE</span><h2>Situazione economica del mese</h2></div>
            <Link to="/app/invoicing" className="dashboard_section_link">Apri Fatturazione</Link>
          </div>
          <MetricRows rows={financeRows} />
        </section>

        <section className="dashboard_section_card">
          <div className="dashboard_section_header">
            <div><span className="dashboard_section_kicker">ANAGRAFICHE</span><h2>Clienti</h2></div>
            <Link to="/app/customers" className="dashboard_section_link">Apri Clienti</Link>
          </div>
          <MetricRows rows={customerRows} />
        </section>
      </div>

      <div className="dashboard_three_column_layout">
        <section className="dashboard_section_card">
          <div className="dashboard_section_header">
            <div><span className="dashboard_section_kicker">DOCUMENTI</span><h2>Mix fiscale mese</h2></div>
            <span className="dashboard_section_period">{formatNumber(financial.freeInvoices)} fatture libere</span>
          </div>
          <div className="dashboard_metric_rows">
            {(dashboardData.fiscalMix || []).map((item) => (
              <div className="dashboard_metric_row" key={item.type}><span>{item.label} · {item.count}</span><strong>{formatMoney(item.gross)}</strong></div>
            ))}
            {!(dashboardData.fiscalMix || []).length && <div className="dashboard_empty_state">Nessun documento fiscale nel mese</div>}
          </div>
        </section>

        <section className="dashboard_section_card">
          <div className="dashboard_section_header">
            <div><span className="dashboard_section_kicker">IVA</span><h2>Castelletto mese</h2></div>
            <Link to="/app/prints" className="dashboard_section_link">Apri Stampe</Link>
          </div>
          <div className="dashboard_compact_table dashboard_vat_table">
            <div className="dashboard_compact_table_head"><span>Aliquota</span><span>Imponibile</span><span>Imposta</span></div>
            {(dashboardData.vatBreakdown || []).map((item) => (
              <div className="dashboard_compact_table_row" key={item.rate}><strong>{formatNumber(item.rate, 0)}%</strong><span>{formatMoney(item.taxable)}</span><span>{formatMoney(item.tax)}</span></div>
            ))}
            {!(dashboardData.vatBreakdown || []).length && <div className="dashboard_empty_state">Nessuna IVA contabilizzata nel mese</div>}
          </div>
        </section>

        <section className="dashboard_section_card">
          <div className="dashboard_section_header">
            <div><span className="dashboard_section_kicker">SERVIZI</span><h2>Più addebitati nel mese</h2></div>
            <Link to="/app/services" className="dashboard_section_link">Apri Servizi</Link>
          </div>
          <div className="dashboard_rank_list">
            {(dashboardData.topServices || []).map((item, index) => (
              <div className="dashboard_rank_row" key={item.description}>
                <span className="dashboard_rank_number">{index + 1}</span>
                <div><strong>{item.description}</strong><span>{formatNumber(item.quantity, 0)} quantità</span></div>
                <b>{formatMoney(item.gross)}</b>
              </div>
            ))}
            {!(dashboardData.topServices || []).length && <div className="dashboard_empty_state">Nessun servizio addebitato nel mese</div>}
          </div>
        </section>
      </div>

      <div className="dashboard_two_column_layout">
        <section className="dashboard_section_card">
          <div className="dashboard_section_header">
            <div><span className="dashboard_section_kicker">AGENDA</span><h2>Prossimi movimenti</h2></div>
            <span className="dashboard_section_period">Oggi + 2 giorni</span>
          </div>
          <div className="dashboard_event_list">
            {(dashboardData.upcomingEvents || []).map((event, index) => (
              <div className="dashboard_event_row" key={`${event.type}-${event.date}-${event.room}-${index}`}>
                <span className={`dashboard_event_type is_${String(event.type || "").toLowerCase()}`}>{event.type}</span>
                <div><strong>{event.customer}</strong><span>Camera {event.room || "-"} · {formatDateTime(event.date)}</span></div>
                <b>{STATUS_LABELS[event.status] || event.status || ""}</b>
              </div>
            ))}
            {!(dashboardData.upcomingEvents || []).length && <div className="dashboard_empty_state">Nessun movimento nei prossimi giorni</div>}
          </div>
        </section>

        <section className="dashboard_section_card">
          <div className="dashboard_section_header">
            <div><span className="dashboard_section_kicker">SINTESI</span><h2>Indicatori rapidi</h2></div>
            <span className="dashboard_section_period">360°</span>
          </div>
          <MetricRows rows={(dashboardData.quickFacts || []).map((item) => ({ label: item.label, value: item.value }))} />
        </section>
      </div>

      <section className="dashboard_section_card">
        <div className="dashboard_section_header">
          <div><span className="dashboard_section_kicker">REPARTI</span><h2>Situazione operativa</h2></div>
          <div className="dashboard_live_chip">{loading ? "CARICO" : refreshing ? "AGGIORNO" : "LIVE"}</div>
        </div>
        <div className="dashboard_activity_table">
          <div className="dashboard_activity_head"><span>Reparto</span><span>Attività</span><span>Priorità</span><span>Stato</span></div>
          {(dashboardData.activities || []).map((row) => (
            <div className="dashboard_activity_row" key={row.join("-")}>
              <span>{row[0]}</span><span>{row[1]}</span><strong className={toneClass(row[2])}>{row[2]}</strong><span className="dashboard_status_badge">{row[3]}</span>
            </div>
          ))}
          {!(dashboardData.activities || []).length && <div className="dashboard_empty_state">Nessuna attività disponibile</div>}
        </div>
      </section>
    </section>
  );
}
